import { getWordList, LATEST_WORDS_VERSION, type WordList } from '@wbwl/words'
import {
  BURN_PATTERNS, BURN_ROWS, EDITION_TIME_ZONE, GOLD_TIERS, LAUNCH_DATE, MAX_GOLD_RARE, MAX_GOLD_VOWELS,
  PATTERN_BAG_FROM, PATTERN_DAYS_FROM, PATTERN_SPACING, RARE_LETTERS, ROWS, RULES_VERSION, VOWELS, WORD_LENGTH,
} from './constants.ts'
import { rngFromSeed } from './random.ts'

export type GoldTier = 1 | 2 | 3
/** A burn tile at [row, column], both counted from 0. */
export type Burn = readonly [row: number, col: number]

/** How a board's burns are laid out: BURN_ROWS (one or two a row) or one of BURN_PATTERNS. */
export type BurnLayout = 'rows' | 'pattern'

/** A BURN_PATTERNS shape in one of its places on the board (patternPlacements). */
export interface Placement {
  readonly pattern: string
  readonly burns: readonly Burn[]
}

/** What a board is made of: gold letters with their tiers, and the burn tiles. */
export interface Board {
  readonly gold: Readonly<Record<string, GoldTier>>
  readonly burns: readonly Burn[]
  /** The BURN_PATTERNS shape the burns form; absent when they're laid out by row. */
  readonly pattern?: string
}

/** A board plus what's needed to rebuild and replay it. Plain JSON, so it can be stored as is. */
export interface Edition extends Board {
  /** Edition No., counted in days from LAUNCH_DATE. null for unranked random boards. */
  readonly number: number | null
  /** YYYY-MM-DD, or null for random boards. */
  readonly date: string | null
  readonly seed: string
  readonly rulesVersion: number
  readonly wordsVersion: number
}

const DAY_MS = 86_400_000

/**
 * Builds a board from a seed. A port of the prototype's generator: with the 'rows' layout, burn
 * counts per row come from BURN_ROWS and two burns in a row are never side by side; with
 * 'pattern', the burns form one of BURN_PATTERNS, picked at random; given a Placement, the burns
 * are that and the seed only picks the gold. Gold letters take GOLD_TIERS with at most
 * MAX_GOLD_VOWELS vowels and MAX_GOLD_RARE of RARE_LETTERS.
 */
export function generateBoard(seed: string, layout: BurnLayout | Placement = 'rows'): Board {
  const rnd = rngFromSeed(seed)
  const { burns, pattern } = typeof layout === 'object'
    ? { burns: [...layout.burns], pattern: layout.pattern }
    : layout === 'pattern' ? patternBurns(rnd) : rowBurns(rnd)

  const gold: Record<string, GoldTier> = {}
  const isVowel = (l: string) => VOWELS.includes(l)
  const isRare = (l: string) => RARE_LETTERS.includes(l)
  for (const tier of GOLD_TIERS) {
    let letter: string
    do {
      letter = String.fromCharCode(65 + Math.floor(rnd() * 26))
    } while (
      gold[letter] ||
      (isVowel(letter) && Object.keys(gold).filter(isVowel).length >= MAX_GOLD_VOWELS) ||
      (isRare(letter) && Object.keys(gold).filter(isRare).length >= MAX_GOLD_RARE)
    )
    gold[letter] = tier
  }
  return pattern ? { gold, burns, pattern } : { gold, burns }
}

function shuffle<T>(items: T[], rnd: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[items[i], items[j]] = [items[j], items[i]]
  }
  return items
}

function rowBurns(rnd: () => number): { burns: Burn[]; pattern?: string } {
  const counts = [...BURN_ROWS.counts]
  if (BURN_ROWS.shuffle) shuffle(counts, rnd)
  const burns: Burn[] = []
  counts.forEach((count, row) => {
    const cols: number[] = []
    while (cols.length < count) {
      const col = Math.floor(rnd() * WORD_LENGTH)
      if (cols.some((x) => Math.abs(x - col) <= 1)) continue
      cols.push(col)
    }
    cols.sort((a, b) => a - b).forEach((col) => burns.push([row, col]))
  })
  return { burns }
}

function patternBurns(rnd: () => number): { burns: Burn[]; pattern: string } {
  const { name, rows, upright } = BURN_PATTERNS[Math.floor(rnd() * BURN_PATTERNS.length)]
  const mirror = rnd() < 0.5
  const flip = !upright && rnd() < 0.5
  const burns: Burn[] = []
  for (let row = 0; row < ROWS; row++) {
    const line = rows[flip ? ROWS - 1 - row : row]
    for (let col = 0; col < WORD_LENGTH; col++) {
      if (line[mirror ? WORD_LENGTH - 1 - col : col] === '#') burns.push([row, col])
    }
  }
  return { burns, pattern: name }
}

/** Pattern days alternate with per-row days from PATTERN_DAYS_FROM on. */
export function burnLayoutFor(number: number): BurnLayout {
  return number >= PATTERN_DAYS_FROM && (number - PATTERN_DAYS_FROM) % 2 === 0 ? 'pattern' : 'rows'
}

/** A shape's burns as drawn ('#' is a burn), in row then column order. */
function draw(rows: readonly string[]): Burn[] {
  return rows.flatMap((line, row) => [...line].flatMap((ch, col) => (ch === '#' ? [[row, col] as Burn] : [])))
}

const mirror = (lines: readonly string[]) => lines.map((line) => [...line].reverse().join(''))
const flip = (lines: readonly string[]) => [...lines].reverse()

/** A shape's drawing and its `alt`, each as drawn and mirrored, and flipped top to bottom if asked. */
function turns(shape: (typeof BURN_PATTERNS)[number], flipped: boolean): (readonly string[])[] {
  return (shape.alt ? [shape.rows, shape.alt] : [shape.rows]).flatMap((d) => (flipped ? [flip(d), flip(mirror(d))] : [d, mirror(d)]))
}

/** Drawings slid to every row and column they fit in, keyed by their burns. */
function slides(drawings: readonly (readonly string[])[]): Map<string, Burn[]> {
  const sets = new Map<string, Burn[]>()
  for (const burns of drawings.map(draw)) {
    const rows = burns.map((b) => b[0])
    const cols = burns.map((b) => b[1])
    for (let dr = -Math.min(...rows); dr < ROWS - Math.max(...rows); dr++) {
      for (let dc = -Math.min(...cols); dc < WORD_LENGTH - Math.max(...cols); dc++) {
        const moved = burns.map(([r, c]) => [r + dr, c + dc] as Burn)
        sets.set(moved.join(' '), moved)
      }
    }
  }
  return sets
}

const placements = new Map<string, Burn[][]>()

/**
 * Every place a BURN_PATTERNS shape can sit: its drawing and its `alt`, each as drawn, mirrored
 * and (unless upright) flipped, slid to every row and column it fits in. Flipping never makes
 * another shape: a flipped Comb is a Crown, so those placements are the Crown's.
 */
export function patternPlacements(name: string): Burn[][] {
  let found = placements.get(name)
  if (!found) {
    const shape = BURN_PATTERNS.find((p) => p.name === name)
    if (!shape) throw new RangeError(`Not a burn pattern: ${name}`)
    const own = slides(turns(shape, false))
    if (!shape.upright) {
      const others = new Set(BURN_PATTERNS.filter((p) => p !== shape).flatMap((p) => [...slides(turns(p, false)).keys()]))
      for (const [key, burns] of slides(turns(shape, true))) if (!others.has(key)) own.set(key, burns)
    }
    found = [...own.values()]
    placements.set(name, found)
  }
  return found
}

let earlyDays: Board[] | null = null

/** The pattern days before PATTERN_BAG_FROM, whose seeds picked their shapes at random. */
function earlyPatternDays(): Board[] {
  earlyDays ??= Array.from({ length: (PATTERN_BAG_FROM - PATTERN_DAYS_FROM) / 2 }, (_, i) =>
    generateBoard(seedFor(editionDate(PATTERN_DAYS_FROM + 2 * i)), 'pattern'))
  return earlyDays
}

const rounds: string[][] = []

/**
 * Round r of the bag: every shape once, in an order seeded by r. Round 0 leaves out the shapes the
 * early pattern days used. A shape among the last PATTERN_SPACING of a round swaps out of the next
 * round's first PATTERN_SPACING.
 */
function bagRound(r: number): readonly string[] {
  while (rounds.length <= r) {
    const k = rounds.length
    const used = new Set(earlyPatternDays().map((b) => b.pattern))
    const order = shuffle(BURN_PATTERNS.map((p) => p.name).filter((name) => k > 0 || !used.has(name)), rngFromSeed(`pattern-bag-${k}`))
    if (k > 0) {
      const recent = new Set(rounds[k - 1].slice(-PATTERN_SPACING))
      for (let i = 0; i < PATTERN_SPACING; i++) {
        if (!recent.has(order[i])) continue
        const j = order.findIndex((name, at) => at >= PATTERN_SPACING && !recent.has(name))
        ;[order[i], order[j]] = [order[j], order[i]]
      }
    }
    rounds.push(order)
  }
  return rounds[r]
}

/**
 * Where a shape sits in round r: its placements in an order seeded by its name, one step on each
 * time it comes up, so it never sits where it did the time before. Placements the early pattern
 * days used go last.
 */
function placementIn(name: string, r: number): readonly Burn[] {
  const used = new Set(earlyPatternDays().map((b) => b.burns.join(' ')))
  const order = shuffle([...patternPlacements(name)], rngFromSeed(`pattern-placements-${name}`))
  const queue = [...order.filter((b) => !used.has(b.join(' '))), ...order.filter((b) => used.has(b.join(' ')))]
  // Shapes the early pattern days used sit out round 0, so round 1 is their first time from the bag.
  const times = earlyPatternDays().some((b) => b.pattern === name) ? r - 1 : r
  return queue[times % queue.length]
}

/** The shape and placement a pattern day deals from the bag, from PATTERN_BAG_FROM on; null on other days. */
export function patternFor(number: number): Placement | null {
  if (number < PATTERN_BAG_FROM || burnLayoutFor(number) !== 'pattern') return null
  let i = (number - PATTERN_BAG_FROM) / 2
  for (let r = 0; ; r++) {
    const round = bagRound(r)
    if (i < round.length) return { pattern: round[i], burns: placementIn(round[i], r) }
    i -= round.length
  }
}

const seedFor = (date: string, attempt = 0) => (attempt ? `edition-${date}#${attempt}` : `edition-${date}`)

/**
 * The edition for a date (YYYY-MM-DD). Its seed is `edition-<date>`, as in the prototype. If the
 * publish job rejects a board (say, its best score is unusually low), pass attempt 1, 2, … for a
 * fresh seed; the seed stored on the edition keeps it reproducible. The date's number decides the
 * burn layout (burnLayoutFor), and from PATTERN_BAG_FROM the shape and its placement (patternFor),
 * so a retry keeps them and only re-rolls what the seed picks.
 */
export function generateEdition(date: string, options: { attempt?: number } = {}): Edition {
  const number = editionNumber(date)
  if (number < 0) throw new RangeError(`${date} is before the first edition (${LAUNCH_DATE})`)
  const seed = seedFor(date, options.attempt)
  return {
    number, date, seed, rulesVersion: RULES_VERSION, wordsVersion: LATEST_WORDS_VERSION,
    ...generateBoard(seed, patternFor(number) ?? burnLayoutFor(number)),
  }
}

/** An unranked board from any seed (the prototype's "Random board"). */
export function randomEdition(seed: string, layout: BurnLayout = 'rows'): Edition {
  return { number: null, date: null, seed, rulesVersion: RULES_VERSION, wordsVersion: LATEST_WORDS_VERSION, ...generateBoard(seed, layout) }
}

/**
 * The sample edition used on every design board. Hand-made: it is not what the seed for its date
 * generates. It keeps rules v1, the scoring its design-board numbers (best 1,088) were made with.
 */
export const SAMPLE_EDITION: Edition = deepFreeze({
  number: 412,
  date: '2026-10-18',
  seed: 'sample-412',
  rulesVersion: 1,
  wordsVersion: 1,
  gold: { R: 1, N: 1, T: 1, C: 2, M: 2, P: 2, K: 3, Y: 3 },
  burns: [[0, 0], [0, 3], [1, 4], [2, 1], [2, 3], [3, 2], [4, 0], [4, 4], [5, 2]],
})

export function editionNumber(date: string): number {
  return Math.round((parseDate(date) - parseDate(LAUNCH_DATE)) / DAY_MS)
}

export function editionDate(number: number): string {
  if (!Number.isInteger(number) || number < 0) throw new RangeError(`Not an edition number: ${number}`)
  return new Date(parseDate(LAUNCH_DATE) + number * DAY_MS).toISOString().slice(0, 10)
}

/** Which edition date it is at an instant, in EDITION_TIME_ZONE by default. */
export function editionDateAt(instant: Date = new Date(), timeZone: string = EDITION_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(instant)
  const part = (type: string) => parts.find((p) => p.type === type)?.value
  return `${part('year')}-${part('month')}-${part('day')}`
}

/** Burn columns in a row, left to right. */
export function burnCols(board: Board, row: number): number[] {
  return board.burns.filter((b) => b[0] === row).map((b) => b[1]).sort((a, b) => a - b)
}

export function isBurn(board: Board, row: number, col: number): boolean {
  return board.burns.some((b) => b[0] === row && b[1] === col)
}

/** The word list an edition was published with. */
export function wordListFor(edition: Pick<Edition, 'wordsVersion'>): WordList {
  return getWordList(edition.wordsVersion)
}

function parseDate(date: string): number {
  const ms = /^\d{4}-\d{2}-\d{2}$/.test(date) ? Date.parse(`${date}T00:00:00Z`) : NaN
  if (Number.isNaN(ms) || new Date(ms).toISOString().slice(0, 10) !== date) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`)
  return ms
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const v of Object.values(value)) deepFreeze(v)
    Object.freeze(value)
  }
  return value
}
